/**
 * Mapeamento `tipos_alteracao` → campos do form que devem ficar editaveis.
 *
 * Sessao 2026-05-11 (item 2 Fase 2). Decisao do cliente:
 * - `endereco`: libera so o campo endereco da empresa. Sub-checkbox opcional
 *   no StepEmpresa permite incluir foro + cidade_assinatura (na pratica
 *   quase sempre acompanha, mas nao e obrigatorio).
 * - `razao_social`: libera SO' razao_social.
 * - `nome_fantasia`: tipo proprio desde 2026-08-13 (antes era carona da
 *   razao_social). Quem muda os dois marca os dois — e sao dois atos distintos
 *   pro DREI, com clausulas alteradoras separadas.
 * - `capital`: libera capital_social + capital_social_extenso.
 * - `objeto_social`: libera objeto_social + cnaes (sao interligados).
 * - `administracao` e `quadro_societario`: nao listam campos da empresa —
 *   sao tratados via Step Socios (acao + redistribuicao + tipo_vinculo).
 * - `outras`: wildcard — libera todos os campos da empresa. Operador decide
 *   o que muda no texto livre das Instrucoes IA.
 *
 * Em **constituicao** nada e bloqueado — todos os campos editaveis.
 */
import type { TipoAlteracao } from '../../../../hooks/useSocietario';

/**
 * Identificador de campos do form (StepEmpresa). Mantemos como union literal
 * pra catar erros de typo em compile-time.
 *
 * Bloco 13 v3 alteração — sub-campos do `endereco_estruturado` (7 campos
 * PF/sede PJ) adicionados pra cobrir o cenário em que o operador edita
 * endereço estruturado em vez do `endereco` textarea legacy. Quando
 * `tipo_alteracao='endereco'` está marcado, todos os 7 ficam editáveis.
 */
export type CampoEmpresa =
  | 'razao_social'
  | 'nome_fantasia'
  | 'cnpj'
  | 'capital_social'
  | 'capital_qtd_quotas'
  | 'capital_valor_quota'
  | 'objeto_social'
  | 'cnaes'
  | 'endereco'
  | 'endereco_estruturado.logradouro'
  | 'endereco_estruturado.numero'
  | 'endereco_estruturado.complemento'
  | 'endereco_estruturado.bairro'
  | 'endereco_estruturado.cidade'
  | 'endereco_estruturado.uf'
  | 'endereco_estruturado.cep'
  | 'foro'
  | 'foro_cidade'
  | 'foro_uf'
  | 'cidade_assinatura'
  | 'data_inicio_atividades'
  | 'junta_comercial_estado'
  | 'junta_comercial_sigla'
  | 'nire'
  | 'data_constituicao'
  | 'numero_alteracao'
  | 'razao_social_consolidada';

const CAMPOS_ENDERECO_ESTRUTURADO: CampoEmpresa[] = [
  'endereco_estruturado.logradouro',
  'endereco_estruturado.numero',
  'endereco_estruturado.complemento',
  'endereco_estruturado.bairro',
  'endereco_estruturado.cidade',
  'endereco_estruturado.uf',
  'endereco_estruturado.cep',
];

const MAPA_TIPO_PARA_CAMPOS: Record<TipoAlteracao, CampoEmpresa[]> = {
  // capital_qtd_quotas + capital_valor_quota acompanham capital_social — em
  // v3 alteração o patch `substituir_campo(capital_social)` lê valor_novo
  // como Decimal único, mas re-render do quadro precisa qtd+valor_unitario
  // coerentes (persistidos no PATCH antes de /gerar).
  capital: ['capital_social', 'capital_qtd_quotas', 'capital_valor_quota'],
  // Sub-campos do endereco_estruturado liberados junto com `endereco` legacy
  // — em v3 alteração o backend lê endereco_estruturado se preenchido, senão
  // cai pro endereco textarea.
  endereco: ['endereco', ...CAMPOS_ENDERECO_ESTRUTURADO],
  // `nome_fantasia` SAIU daqui em 2026-08-13 (virou tipo proprio). Marcar
  // "Razão social" nao libera mais a fantasia: o que voce marca e' o que voce
  // muda, e some o patch fantasma de razao social que existia so' pra destravar
  // o campo ao lado.
  razao_social: ['razao_social'],
  nome_fantasia: ['nome_fantasia'],
  objeto_social: ['objeto_social', 'cnaes'],
  administracao: [],
  // O texto vem do chat das cláusulas (15/10), não de campo da empresa.
  clausulas: [],
  quadro_societario: [],
  // Dados de socio mudam no passo Socios, nao na empresa.
  dados_socio: [],
  // Nao libera campo nenhum: natureza juridica nao e coluna do contrato e o
  // wizard nao gera esse ato (so a transformacao EI/MEI -> LTDA, que e operacao
  // propria). Existe pra legalizacao marcar o processo e derivar a Rota A.
  natureza_juridica: [],
  // Os endereços das filiais têm campo próprio (`filiais`), fora da empresa.
  abertura_filial: [],
  // Idem: as filiais encerradas têm campo próprio (`filiais_fechar`).
  extincao_filial: [],
  // Idem: as filiais que mudam de endereço (`filiais_alterar`).
  alteracao_filial: [],
  outras: [],
};

/**
 * Campos **sempre obrigatorios em alteracao** mesmo sem tipo marcado.
 * Sao dados estruturais do contrato (numero da alteracao, registro etc),
 * nao representam mudanca na empresa.
 */
const CAMPOS_INTRINSECOS_ALTERACAO: CampoEmpresa[] = [
  'cnpj',
  'foro',
  'cidade_assinatura',
  'junta_comercial_estado',
  'junta_comercial_sigla',
  'nire',
  'numero_alteracao',
];
// `data_constituicao` saiu desta lista em 2026-08-06 (pedido da Juliana): o campo
// deixou de ser renderizado em alteracao, porque o motor v3 nao le esse valor.
// Segue no type `CampoEmpresa` — a TRANSFORMACAO ainda o coleta (data de
// arquivamento do EI, `StepEmpresarioIndividual`), e la nada e' bloqueado.
//
// NOTA (2026-08-06): esta lista e' o UNICO escape hatch pra extracao errada em
// alteracao. Nao existe gate de confianca por campo (o `VIGENTE_ILEGIVEL` barra
// documento ilegivel, nao valor errado) e o wildcard `outras` esta `oculto` +
// aborta a geracao v3-only. Antes de tirar mais algum campo daqui, lembre que
// nenhum `tipo_alteracao` destrava cnpj/nire/junta/foro.

/**
 * Decide se um campo deve estar editavel dado o estado atual do form.
 *
 * - `tipo_operacao === 'constituicao'` → todos editaveis.
 * - `outras` em `tipos_alteracao` → todos editaveis (wildcard).
 * - Campo intrinseco de alteracao → sempre editavel.
 * - Senao → editavel apenas se algum tipo marcado lista esse campo.
 *
 * O sub-checkbox "Atualizar tambem foro e cidade de assinatura?" no
 * StepEmpresa expande a lista do `endereco` em runtime — passamos a flag
 * `atualizarForoComEndereco` aqui pra evitar dependencias circulares.
 */
export interface CampoEditavelArgs {
  tipoOperacao: 'constituicao' | 'alteracao';
  tiposAlteracao: TipoAlteracao[];
  atualizarForoComEndereco?: boolean;
}

export function isCampoEditavel(campo: CampoEmpresa, args: CampoEditavelArgs): boolean {
  if (args.tipoOperacao === 'constituicao') return true;
  if (args.tiposAlteracao.includes('outras')) return true;
  if (CAMPOS_INTRINSECOS_ALTERACAO.includes(campo)) return true;

  // razao_social_consolidada so faz sentido se razao_social muda — mesmo
  // padrao usado no StepEmpresa (item 20 Fase 1).
  if (campo === 'razao_social_consolidada') {
    return args.tiposAlteracao.includes('razao_social');
  }

  // Sub-checkbox no StepEmpresa: quando endereco e marcado e usuario opta
  // por estender, foro e cidade_assinatura tambem entram.
  if (
    args.atualizarForoComEndereco &&
    args.tiposAlteracao.includes('endereco') &&
    (campo === 'foro' || campo === 'cidade_assinatura')
  ) {
    return true;
  }

  return args.tiposAlteracao.some(tipo => MAPA_TIPO_PARA_CAMPOS[tipo].includes(campo));
}

/**
 * Devolve a lista de tipos_alteracao que liberam esse campo (pra render do
 * tooltip explicando porque ele esta readonly).
 */
export function tiposQueLiberam(campo: CampoEmpresa): TipoAlteracao[] {
  const tipos: TipoAlteracao[] = [];
  (Object.keys(MAPA_TIPO_PARA_CAMPOS) as TipoAlteracao[]).forEach(tipo => {
    if (MAPA_TIPO_PARA_CAMPOS[tipo].includes(campo)) {
      tipos.push(tipo);
    }
  });
  return tipos;
}
